import React, {useEffect, useRef} from 'react';
import {Animated, Easing, View} from 'react-native';

import CCText from '../../../Atoms/CCText';

import {F} from '../courseTheme';

type Props = {
  /** The sentence being spoken right now. */
  text: string;
};

const DOTS = 3;

/**
 * The line the coach is saying, while it is saying it.
 *
 * The board moves on the same beat, so this is what ties the two together: it
 * is the caption for the move that just appeared. Without it a piece slides
 * across a silent-looking board and the student has to work out which of the
 * coach's sentences it belonged to.
 *
 * The pulsing dots are deliberately not a spinner — a spinner means "waiting",
 * and nothing here is waiting. They mean "still talking".
 */
export default function CourseSpeakingLine({text}: Props) {
  const dots = useRef(
    Array.from({length: DOTS}, () => new Animated.Value(0.35)),
  ).current;

  useEffect(() => {
    // Each dot swells and fades slightly behind the one before it.
    const loops = dots.map((value, i) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(i * 160),
          Animated.timing(value, {
            toValue: 1,
            duration: 420,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
          Animated.timing(value, {
            toValue: 0.35,
            duration: 420,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
          Animated.delay((DOTS - i) * 160),
        ]),
      ),
    );
    loops.forEach(l => l.start());
    return () => loops.forEach(l => l.stop());
  }, [dots]);

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        backgroundColor: F.hintBg,
        borderLeftWidth: 3,
        borderLeftColor: F.hintFg,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
      }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 3,
          paddingTop: 6,
        }}>
        {dots.map((value, i) => (
          <Animated.View
            key={i}
            style={{
              width: 5,
              height: 5,
              borderRadius: 999,
              backgroundColor: F.hintFg,
              opacity: value,
              transform: [
                {
                  scale: value.interpolate({
                    inputRange: [0.35, 1],
                    outputRange: [0.8, 1.25],
                  }),
                },
              ],
            }}
          />
        ))}
      </View>
      <CCText
        style={{
          flex: 1,
          fontSize: 14,
          fontWeight: '600',
          color: F.brownDark,
          lineHeight: 21,
        }}>
        {text}
      </CCText>
    </View>
  );
}
